import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import { AccessProfileCard } from '../../../components/access-profile/AccessProfileCard';
import AssignedTab from '../../../components/assignWizard/AssignedTab';
import AssignModalTwo from '../../../components/assignWizard/AssignModal';
import ManageButton from '../../../components/assignWizard/ManageButton';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { ACCESS_TYPES } from '../../../constants/access-constants';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { AccessProfile as AccessProfileModel } from '../../../models/AccessProfile';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import AccessGroupColumns from '../../../models/ColumnDefinitions/AccessGroupColumns';
import EnrolmentColumns from '../../../models/ColumnDefinitions/EnrolmentColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import AccessAreaSearch from '../../../models/search/AccessAreaSearch';
import AccessGroupSearch from '../../../models/search/AccessGroupSearch';
import AccessTimePatternSearch from '../../../models/search/AccessTimePatternSearch';
import CategorySearch from '../../../models/search/CategorySearch';
import CompanySearch from '../../../models/search/CompanySearch';
import DepartmentSearch from '../../../models/search/DepartmentSearch';
import EnrolmentSearch from '../../../models/search/EnrolmentSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { TableColumn } from '../../../models/table/TableColumn';
import { TabSummary } from '../../../models/TabSummary';
import { extendColumnDefinition, extendSearchColumn, hideColumns } from '../../../models/utilities/columnHelper';
import { getAccessAreaNamesWithPagination } from '../../../services/accessAreaService';
import { assignUnassignAccessProfileGroups, getAccessGroupIdsForAccessProfile } from '../../../services/accessGroupProfileService';
import { getAccessGroupsForAccessProfile, getAccessGroupsNamesWithPagination, getAllAccessGroups } from '../../../services/accessGroupService';
import { editAccessProfile, getAccessProfileById, getAccessProfilesNamesWithPagination } from '../../../services/accessProfileService';
import { getAccessTimePatternNamesWithPagination } from '../../../services/accessTimePatternService';
import { getCategoryNameWithPagination } from '../../../services/categoryService';
import { getCompanyNamesWithPagination } from '../../../services/companyService';
import { getDepartmentNamesWithPagination } from '../../../services/departmentService';
import { getAccessProfileAndGroupCounts, getEnrolmentNamesWithPagination, getEnrolments, getMasterForAccessProfiles } from '../../../services/enrolmentService';
import { assignAndUnassignMasterGroupProfile, getMasterIdsForAccessProfile } from '../../../services/masterGroupProfileService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { getDayNames } from '../../../utilities/DateUtils';
import { appendFilter } from '../../../utilities/filters';
import { PaginationModel } from '@/models/Pagination';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/enrolment/access-profiles';
const itemPath = '/access-control/enrolment/access-profile/';

const initialItem: AccessProfileModel = {
    id: 0,
    name: '',
    description: '',
    accessGroups: [],
    enabled: false,
    totalGroups: 0,
    totalFloorGroups: 0,
    totalMasters: 0
}

type EntityType = "ACCESS_GROUP" | "FLOOR_GROUP" | "MEMBERS";

const AccessProfile: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.accessGroup), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.floorGroup), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.members), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<AccessProfileModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
    const [beingAssigned, setBeingAssigned] = useState<EntityType>("ACCESS_GROUP");
    const AccessGroupLookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const MemberLookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const extendLookups = (lookup: Lookup[], extendUsing: string): Lookup[] => {
        return lookup.map((current) => {
            if (current.name === LookupTypes.days) {
                return current;
            }
            return {
                ...current,
                name: extendUsing + '.' + current.name,
            }
        })
    };

    const topic: Topic = {
        accessor: 'apName',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.accessProfiles),
    }

    const reloadPage = async () => {
        try {
            let accessProfile: any;
            if (itemId) {
                accessProfile = await getAccessProfileById(itemId);

                setSelectedItem({ label: accessProfile.name, value: accessProfile.id.toString(), isDefault: accessProfile.isDefault })
                setCurrentItem(accessProfile);
                // update the tab badge counts
                const updatedTabs =
                    updateTabBadgeTotal(0, accessProfile?.totalGroups,
                        updateTabBadgeTotal(1, accessProfile?.totalFloorGroups,
                            updateTabBadgeTotal(2, accessProfile?.totalMasters, initialTabs)));

                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.accessProfiles) },
                { value: itemPath + `${itemId}`, displayName: `${accessProfile?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const extendAssignedAccessGroupsColumns = (): TableColumn[] => {
        const columnsToExtend = AccessGroupColumns.filter(column => column.accessor !== "days");
        const extendedColumns = extendColumnDefinition(columnsToExtend, 'accessGroup');
        const daysColumn = AccessGroupColumns.find(column => column.accessor === "days");
        return daysColumn ? [...extendedColumns, daysColumn] : extendedColumns;
    };

    const handleManageClick = (key: EntityType) => {
        setShowAssignModal(true);
        setBeingAssigned(key);
    };

    const tabContentList: ContentWrapper[] = [
        {
            content: <AssignedTab
                key={"AccessGroup-tab"}
                column={hideColumns(extendAssignedAccessGroupsColumns(), ['enrolments'])}
                searchColumn={extendSearchColumn(AccessGroupSearch, 'accessGroup')}
                id={itemId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                dataMethod={(listParameters?: ListQuery) => getAccessGroupsForAccessProfile(itemId, translate, {...listParameters, filters: appendFilter(ACCESS_TYPES.ACCESS_GROUP, "Type", "accessGroup.agType", listParameters?.filters ?? [])})}
                title={translate.byKey(TranslationKeys.assignedAccessGroups)}>
                <ManageButton onClick={() => handleManageClick("ACCESS_GROUP")} />
            </AssignedTab>
        },
        {
            content: <AssignedTab
                key={"FloorGroup-tab"}
                column={hideColumns(extendAssignedAccessGroupsColumns(), ['enrolments'])}
                searchColumn={extendSearchColumn(AccessGroupSearch, 'accessGroup')}
                id={itemId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                dataMethod={(listParameters?: ListQuery) => getAccessGroupsForAccessProfile(itemId, translate, { ...listParameters, filters: appendFilter(ACCESS_TYPES.FLOOR_GROUP, "Type", "accessGroup.agType", listParameters?.filters ?? []) })}
                title={translate.byKey(TranslationKeys.assignedFloorGroups)}>
                <ManageButton onClick={() => handleManageClick("FLOOR_GROUP")} />
            </AssignedTab>
        },
        {
            content: <AssignedTab
                key={"Members-tab"}
                column={hideColumns(extendColumnDefinition(EnrolmentColumns, 'master'), ["companyName", "email"])}
                searchColumn={extendSearchColumn(EnrolmentSearch, "master")}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getMasterForAccessProfiles(translate, itemId, listParameters)}
                lookup={extendLookups(MemberLookup, 'master')}
                title={translate.byKey(TranslationKeys.assignedMembers)}>
                <ManageButton onClick={() => {handleManageClick("MEMBERS")}} />
            </AssignedTab>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveAccessProfile = (values: any) => {
        const accessGroup: AccessProfileModel = {
            ...currentItem,
            name: values.name,
            description: values.description,
            enabled: values.enabled,
        };

        saveItem(accessGroup);
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, setOldSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOldSelectedRows: React.Dispatch<React.SetStateAction<string[]>>)  => {
        try {
            let ids: string[] = [], pageNumber = 1, pagination: PaginationModel, rows: any = [];

            if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP") {
                do {
                    const accessProfilesGroups = await getAccessGroupsForAccessProfile(itemId, translate, {pageSize: 100, goToPage: pageNumber});
                    ids = ids.concat(accessProfilesGroups.rows.map(accessProfilesGroup => accessProfilesGroup.id.toString()));
                    rows = rows.concat(accessProfilesGroups.rows);
                    pagination = accessProfilesGroups.pagination;
                    pageNumber++;
                } while(pageNumber <= pagination.totalPages); 
            }
            else if (beingAssigned === "MEMBERS") {
                do {
                    const masterForAccessProfiles = await getMasterForAccessProfiles(translate, itemId, {pageSize: 100, goToPage: pageNumber});
                    ids = ids.concat(masterForAccessProfiles.rows.map(masterForAccessProfile => masterForAccessProfile.id.toString()));
                    rows = rows.concat(masterForAccessProfiles.rows);
                    pagination = masterForAccessProfiles.pagination;
                    pageNumber++;
                } while(pageNumber <= pagination.totalPages)
            }
            
            setSelectedIds(ids);
            setSelectedRows(rows);
            setOldSelectedIds(ids);
            setOldSelectedRows(rows);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }

        return tabContentList[0].content;
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const saveItem = (item: AccessProfileModel) => {
        setBusy(true);
        editAccessProfile(item)
            .then(async (value: any) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.accessGroupSaved));
                setCurrentItem(item);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const handleSubmit = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        let responsePromises: Promise<unknown> = Promise.resolve();

        if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP") {
            responsePromises = assignUnassignAccessProfileGroups(toBeAssigned, toBeUnassigned, itemId)
        }
        else if (beingAssigned === "MEMBERS") {
            responsePromises = assignAndUnassignMasterGroupProfile(toBeAssigned, toBeUnassigned, itemId, "ACCESS_PROFILE");
        }

        responsePromises.then(() => {
            reloadPage();
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }
    
    const getDataMethod = (beingAssigned: EntityType, listParameters?:  ListQuery) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return getAllAccessGroups(1, translate, listParameters)
        }else if (beingAssigned === "MEMBERS") {
            return getEnrolments(translate, listParameters)
        }

        return getAllAccessGroups(2, translate, listParameters)
    }

    const getModalTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.assignAccessGroups);
        }
        else if (beingAssigned === "MEMBERS") {
            return translate.byKey(TranslationKeys.assignMembers);
        }
        return translate.byKey(TranslationKeys.assignFloorGroups);
    }

    const getAssigningTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.accessGroups);
        }
        else if (beingAssigned === "MEMBERS") {
            return translate.byKey(TranslationKeys.members);
        }
        return translate.byKey(TranslationKeys.floorGroups);
    }

    return <div className='flex h-full'>
        <TopicContextProvider dataMethod={getAccessProfilesNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveAccessProfile}
                    tabs={tabs}
                    initialValues={currentItem}>
                    <AccessProfileCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignModalTwo
            tableColumns={beingAssigned === "MEMBERS" ? hideColumns(EnrolmentColumns, ["totalTags", "category"]) : hideColumns(AccessGroupColumns, ["days", "startTime", "duration", "enrolments", "accessProfile"])}
            isOpen={showAssignModal}
            dataMethod={(listParameters?: ListQuery) => getDataMethod(beingAssigned, listParameters)}
            lookup={beingAssigned === "MEMBERS" ? MemberLookup: AccessGroupLookup}
            assigningTitle={getAssigningTitle(beingAssigned)}
            searchColumns={beingAssigned === "MEMBERS" ? EnrolmentSearch : AccessGroupSearch}
            onClose={() => { setShowAssignModal(false) }}
            modalTitle={getModalTitle(beingAssigned)}
            getAssignedItems={getAssignedItems}
            handleSubmit={handleSubmit}/>
    </div>
}

export default AccessProfile;

